import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { ChevronRight, Crown, Loader2, Sparkles } from 'lucide-react';
import { Button } from 'components/ui/button';
import { useToast } from 'components/ui/use-toast';
import { cn } from 'lib/utils';
import * as Api from '../../api';
import { BANDS, CRITERION_LABEL_KEY, SPEAKING_CRITERIA, WRITING_CRITERIA, countWords, MIN_WORDS } from '../../constants';
import type { IeltsCriterionScore, IeltsWritingTask } from '../../types';
import { SpeakingClips, type SpeakingClip } from '../report/SpeakingClips';

export type { SpeakingClip };

interface Props {
  attemptId: string;
  questionId: string;
  answerId: string;
  task: IeltsWritingTask | 'SPEAKING';
  /** Writing: the candidate's script. */
  script?: string;
  /** Speaking: the recordings to listen to. */
  recordings?: SpeakingClip[];
  /** Speaking: the platform lets the AI suggest bands for recordings. */
  aiAvailable?: boolean;
  /** Criteria the AI already suggested on submit, if any. */
  suggested?: IeltsCriterionScore[];
  suggestedFeedback?: string;
  canUseAi: boolean;
  onUpgrade: () => void;
  onSkip: () => void;
  onGraded: () => void;
}

/** Task band: the mean of the four criteria, rounded down to a half band (mirrors the API). */
const taskBand = (bands: Array<number | undefined>): number | null =>
  bands.some((b) => b === undefined) ? null : Math.floor(((bands as number[]).reduce((a, b) => a + b, 0) / bands.length) * 2 + 1e-9) / 2;

/**
 * Teacher form for one IELTS-style Writing script or Speaking test: four
 * criterion bands. The AI can pre-fill them (Speaking: from transcripts of the
 * recordings), but nothing counts until the teacher confirms.
 */
export function IeltsBandGrader({
  attemptId,
  questionId,
  answerId,
  task,
  script = '',
  recordings = [],
  aiAvailable = false,
  suggested,
  suggestedFeedback,
  canUseAi,
  onUpgrade,
  onSkip,
  onGraded,
}: Props) {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const speaking = task === 'SPEAKING';
  const keys = speaking ? SPEAKING_CRITERIA : WRITING_CRITERIA[task];
  const [bands, setBands] = useState<Record<string, number | undefined>>({});
  const [feedback, setFeedback] = useState('');
  const [ai, setAi] = useState<{ criteria: IeltsCriterionScore[]; feedback: string; confidence?: string } | null>(
    suggested?.length ? { criteria: suggested, feedback: suggestedFeedback ?? '' } : null,
  );
  const [suggesting, setSuggesting] = useState(false);
  const [saving, setSaving] = useState(false);
  // Speaking: transcripts that came with a fresh suggestion, by recording key.
  const [transcripts, setTranscripts] = useState<Record<string, string>>({});

  useEffect(() => {
    setBands({});
    setFeedback('');
    setTranscripts({});
    setAi(suggested?.length ? { criteria: suggested, feedback: suggestedFeedback ?? '' } : null);
  }, [answerId, suggested, suggestedFeedback]);

  const current = useMemo(() => taskBand(keys.map((k) => bands[k])), [bands, keys]);
  const words = countWords(script);

  const suggest = async () => {
    if (!canUseAi) {
      onUpgrade();
      return;
    }
    setSuggesting(true);
    try {
      const { data } = await Api.SuggestIeltsBands(attemptId, questionId, i18n.language);
      setAi({ criteria: data.criteria, feedback: data.feedback, confidence: data.confidence });
      if (data.transcripts) setTranscripts(data.transcripts);
    } catch (e: any) {
      toast.error(e?.response?.data?.message || t('ai_suggestion_failed'));
    } finally {
      setSuggesting(false);
    }
  };

  const applyAi = () => {
    if (!ai) return;
    setBands(Object.fromEntries(ai.criteria.map((c) => [c.key, c.band])));
    if (!feedback && ai.feedback) setFeedback(ai.feedback);
  };

  const confirm = async () => {
    if (current === null) return;
    setSaving(true);
    try {
      await Api.GradeIeltsBands({
        answerId,
        criteria: keys.map((key) => ({ key, band: bands[key] as number })),
        feedback: feedback.trim() || undefined,
      });
      await queryClient.invalidateQueries({ queryKey: ['grading', 'pending'] });
      onGraded();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || t('exam.grading.save_failed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4 sm:p-6">
        {task === 'SPEAKING' ? (
          <SpeakingClips recordings={recordings.map((r) => ({ ...r, transcript: transcripts[r.key] ?? r.transcript }))} />
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="rounded-full bg-gray-900 px-2.5 py-0.5 text-xs font-semibold text-white">
                {task === 'TASK_1' ? t('exam.writing.task1') : t('exam.writing.task2')}
              </span>
              <span className={cn('text-sm', words < MIN_WORDS[task] ? 'font-medium text-amber-700' : 'text-muted-foreground')}>
                {t('exam.writing.word_count', { count: words })} · {t('exam.writing.minimum', { count: MIN_WORDS[task] })}
              </span>
            </div>

            <div className="rounded-lg border border-border bg-card p-4">
              <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{t('candidate_s_answer')}</p>
              <p className="whitespace-pre-wrap font-serif text-[15px] leading-7 text-foreground">{script}</p>
            </div>
          </>
        )}

        {(!speaking || aiAvailable) && (
        <div className="rounded-lg border border-border bg-background p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="flex items-center gap-1.5 text-sm font-semibold text-foreground">
                <Sparkles className="h-4 w-4 text-muted-foreground" aria-hidden />
                {ai
                  ? t('exam.grading.ai_bands', { band: taskBand(keys.map((k) => ai.criteria.find((c) => c.key === k)?.band))?.toFixed(1) ?? '—' })
                  : t('exam.grading.ai_get')}
              </p>
              {ai?.feedback && <p className="mt-1 text-sm text-foreground/80">{ai.feedback}</p>}
              <p className="mt-1 text-xs text-muted-foreground">
                {suggesting && speaking ? t('exam.grading.ai_listening') : t(speaking ? 'exam.grading.ai_speaking_note' : 'exam.grading.ai_note')}
              </p>
            </div>
            {ai ? (
              <Button size="sm" variant="outline" onClick={applyAi}>
                {t('exam.grading.use_ai')}
              </Button>
            ) : (
              <Button size="sm" variant="outline" onClick={suggest} disabled={suggesting}>
                {suggesting ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                ) : (
                  <>
                    {!canUseAi && <Crown className="mr-1 h-3.5 w-3.5 text-amber-500" aria-hidden />}
                    {t('ai_suggestion')}
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
        )}

        <fieldset className="overflow-hidden rounded-lg border border-border">
          <legend className="sr-only">{t('exam.grading.criteria')}</legend>
          <p className="border-b border-border px-4 py-2.5 text-sm font-semibold text-foreground">{t('exam.grading.criteria')}</p>
          {keys.map((key) => (
            <div key={key} className="flex flex-col gap-2 border-b border-border/60 px-4 py-3 last:border-0 sm:flex-row sm:items-center">
              <label htmlFor={`band-${key}`} className="min-w-0 flex-1 text-sm text-foreground">
                {t(CRITERION_LABEL_KEY[key])}
                {ai && (
                  <span className="ml-2 text-xs text-muted-foreground">
                    {t('exam.grading.ai_value', { band: ai.criteria.find((c) => c.key === key)?.band?.toFixed(1) ?? '—' })}
                  </span>
                )}
              </label>
              <select
                id={`band-${key}`}
                value={bands[key] ?? ''}
                onChange={(e) => setBands((b) => ({ ...b, [key]: e.target.value === '' ? undefined : Number(e.target.value) }))}
                className="h-9 w-full rounded-md border border-input bg-background px-2 font-mono text-sm sm:w-28"
              >
                <option value="">{t('exam.grading.pick_band')}</option>
                {BANDS.slice().reverse().map((b) => (
                  <option key={b} value={b}>
                    {b.toFixed(1)}
                  </option>
                ))}
              </select>
            </div>
          ))}
        </fieldset>

        <div className="overflow-hidden rounded-lg border border-border ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
          <label htmlFor="ielts-feedback" className="block border-b border-border px-4 py-2.5 text-sm font-semibold text-foreground">
            {t('exam.grading.feedback')}
          </label>
          <textarea
            id="ielts-feedback"
            rows={3}
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            placeholder={t('exam.grading.feedback_placeholder')}
            className="w-full resize-y bg-transparent px-4 py-3 text-sm text-foreground outline-none focus-visible:!outline-none placeholder:text-muted-foreground"
          />
        </div>
      </div>

      <div className="flex shrink-0 items-center justify-between gap-3 border-t border-border px-6 py-3.5">
        <div className="text-sm">
          <span className="text-muted-foreground">{t(speaking ? 'exam.grading.speaking_band' : 'exam.grading.task_band')}:</span>{' '}
          <span className="text-base font-bold tabular-nums text-foreground">{current?.toFixed(1) ?? '—'}</span>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={onSkip}>
            {t('grading_skip')}
          </Button>
          <Button size="sm" onClick={confirm} disabled={current === null || saving}>
            {saving && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden />}
            {t('exam.grading.confirm')}
            <ChevronRight className="ml-1 h-4 w-4" aria-hidden />
          </Button>
        </div>
      </div>
    </div>
  );
}

export default IeltsBandGrader;
